import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { CompanyMediaDTO, CompanyOptimizedDTO, VacancyDTO } from "../../types";
import { MediaGallery } from "../common/media-gallery";
import { getMediaURL } from "../../utils/media-utils";
import { useCompanyById } from "../../hooks/use-company.hook";

export function CompanyCard({
    optimizedCompany,
    onClose,
}: {
    optimizedCompany: CompanyOptimizedDTO;
    onClose?: () => void;
}) {
    const { data: company, isPending, isError, refetch } = useCompanyById(optimizedCompany.id);

    const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
    const [descIsOpen, setDescIsOpen] = useState(false);
    const [vacanciesIsOpen, setVacanciesIsOpen] = useState(false);
    const [selectedVac, setSelectedVac] = useState<VacancyDTO | null>(null);

    const dropdownRef = useRef<HTMLDivElement>(null);
    const vacancyRefs = useRef<Map<string, HTMLLIElement>>(new Map());

    const websiteDomain = useMemo(() => {
        if(!company?.site) return null;
        let domain = company.site.replace(/^https?:\/\//, '').split('/')[0].replace(/^www\./, '');

        return domain;
    }, [company?.site]);

    useEffect(() => {
        setDropdownIsOpen(false);
        setDescIsOpen(false);
        setVacanciesIsOpen(false);
        setSelectedVac(null);
    }, [company]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if(dropdownIsOpen && dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setDropdownIsOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [dropdownIsOpen]);

    useLayoutEffect(() => {
        if(selectedVac && vacanciesIsOpen) {
            const timer = setTimeout(() => {
                const element = vacancyRefs.current.get(selectedVac.id);
                if(element) {
                    element.scrollIntoView({ block: "start", behavior: "smooth" });
                }
            }, 270);
            return () => clearTimeout(timer);
        }
    }, [selectedVac, vacanciesIsOpen]);

    const toggleDropdown = useCallback(() => setDropdownIsOpen(prev => !prev), []);
    const toggleDescription = useCallback(() => setDescIsOpen(prev => !prev), []);
    const openVacancies = useCallback(() => setVacanciesIsOpen(true), []);
    const closeVacancies = useCallback(() => setVacanciesIsOpen(false), []);
    const toggleVacancy = useCallback((vac: VacancyDTO) => {
        setSelectedVac(prev => (prev?.id === vac.id ? null : vac));
    }, []);
    const hasVacancyDetails = useCallback((vac: VacancyDTO) => {
        return !!(vac.description || vac.employmentType || vac.salary || vac.workSchedule);
    }, []);

    return (
        <div className={`card popup-animated ${isError ? 'error-modal' : ''}`}>
            <div className="card__accordion--scrollable">
                {/* Header */}
                <div className="card-top">
                    <div className="card-top__left">
                        <button
                            type="button"
                            className={`card-close__btn ${!vacanciesIsOpen ? 'hide' : ''}`}
                            onClick={closeVacancies}
                        >
                            <ion-icon name="chevron-back" />
                        </button>
                        <div className={`card-top__text-box ${vacanciesIsOpen ? 'hidden' : ''}`}>
                            <span className="card-top__another-title">
                                {optimizedCompany.name}
                            </span>
                            {!isError && (
                                <>
                                    {(isPending || !company) ? (
                                        <div className="skeleton-block">
                                            Loading...
                                        </div>
                                    ) : (
                                        <span className="card-top__org-title card-top__another-title--subtitle">
                                            {company.fieldOfActivity}
                                        </span>
                                    )}
                                </>
                            )}
                        </div>
                        <div className={`card-top__text-box ${!vacanciesIsOpen ? 'hidden' : ''}`}>
                            <span className="card-top__another-title">
                                Вакансии
                            </span>
                            <span className="card-top__org-title card-top__another-title--subtitle">
                                {optimizedCompany.name}
                            </span>
                        </div>
                    </div>

                    <button type="button" className="card-close__btn" onClick={onClose}>
                        <ion-icon name="close" />
                    </button>
                </div>
                {!isError ? (
                    <>
                        {/* MAIN INFO */}
                        {(isPending || !company) ? (
                            <>
                                <div className="card-middle grid">
                                    <div className="icon-card skeleton-block">
                                        <span>Loading...</span>
                                    </div>
                                    <div className="icon-card skeleton-block">
                                        <span>Loading...</span>
                                    </div>
                                </div>
                                <div className="card-middle">
                                    <MediaGallery media={[]} isSkeleton={true}/>
                                </div>
                                <div className="card-bottom without-padding">
                                    <div className="card-info skeleton-block">
                                        <span>Адрес</span>
                                        <span>Адрес</span>
                                    </div>
                                    <div className="card-info skeleton-block">
                                        <span>Dude</span>
                                    </div>
                                    <div className="card-info skeleton-block">
                                        <span>Dude</span>
                                        <span>Dude</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            !vacanciesIsOpen  && (
                                <>
                                    {/* Contacts */}
                                    {(company.phone || company.email || company.site) && (
                                        company.site ? (
                                            <div className="card-middle grid">
                                                {(company.phone && company.email) ? (
                                                    <div className="dropdown-wrapper" ref={dropdownRef}>
                                                        <div className="icon-card card-dropdown" onClick={toggleDropdown}>
                                                            <div className="card-dropdown-top">
                                                                <span>Связаться</span>
                                                                <ion-icon name="chevron-down"></ion-icon>
                                                            </div>
                                                        </div>
                                                        <div className={`card-dropdown--list ${!dropdownIsOpen ? 'hidden' : ''}`}>
                                                            <a href={`tel:${company.phone}`} className="card-dropdown--list__item">
                                                                <ion-icon name="call" />
                                                                {company.phone}
                                                            </a>
                                                            <a href={`mailto:${company.email}`} className="card-dropdown--list__item">
                                                                <ion-icon name="mail"></ion-icon>
                                                                <span className="auto-trim">{company.email}</span>
                                                            </a>
                                                        </div>
                                                    </div>
                                                ) : company.phone ? (
                                                    <a href={`tel:${company.phone}`} className="icon-card">
                                                        <ion-icon name="call" />
                                                        {company.phone}
                                                    </a>
                                                ) : company.email ? (
                                                    <a href={`mailto:${company.email}`} className="icon-card">
                                                        <ion-icon name="mail"></ion-icon>
                                                        <span className="auto-trim">{company.email}</span>
                                                    </a>
                                                ) : null}

                                                <a href={company.site.startsWith("http") ? company.site : "https://" + company.site} target="_blank" rel="noreferrer" className="icon-card">
                                                    <ion-icon name="globe-outline" />
                                                    <span className="icon-card--text">{websiteDomain}</span>
                                                </a>
                                            </div>
                                        ) : (
                                            <div className="card-middle grid">
                                                {company.phone && (
                                                    <a href={`tel:${company.phone}`} className="icon-card">
                                                        <ion-icon name="call" />
                                                        {company.phone}
                                                    </a>
                                                )} 
                                                {company.email ? (
                                                    <a href={`mailto:${company.email}`} className="icon-card">
                                                        <ion-icon name="mail"></ion-icon>
                                                        <span className="auto-trim">{company.email}</span>
                                                    </a>
                                                ) : null}
                                            </div>
                                        )
                                    )}

                                    {/* Gallery */}
                                    {company.companyMedias.length > 0 && (
                                        <div className="card-middle">
                                                <MediaGallery media={company.companyMedias.map((cm: CompanyMediaDTO) => ({
                                                type: cm.type,
                                                url: getMediaURL(cm.objectKey)
                                            }))} />
                                        </div>
                                    )}

                                    {/* Info */}
                                    <div className="card-bottom without-padding">
                                        {company.address && (
                                            <div className={`card-info ${(company.vacancies.length || company.description) ? 'info-separator' : ''}`}>
                                                <span>Адрес</span>
                                                <span>{company.address}</span>
                                            </div>
                                        )}
                                        {company.description && (
                                            <div className={`card-info ${company.vacancies.length ? 'info-separator' : ''}`}>
                                                <button
                                                    type="button"
                                                    className="card-info--with-btn"
                                                    onClick={toggleDescription}
                                                >
                                                    <div className="card-info__bottom">
                                                        <span>Описание</span>
                                                        <ion-icon className={`card__accordion--dropdown__chevron ${descIsOpen ? 'open' : ''}`} name="chevron-forward" />
                                                    </div>
                                                </button>

                                                <div className={`card__accordion--dropdown ${descIsOpen ? "" : "hidden"}`}>
                                                    <div>{company.description}</div>
                                                </div>
                                            </div>
                                        )}
                                        {company.vacancies.length > 0 && (
                                            <div className="card-info row">
                                                <div className="card-info">
                                                    <span>Вакансии</span>

                                                    <div className="card-info__bottom">
                                                        <span className="card-info--black">
                                                            {company.vacancies.length} вакансий
                                                        </span>

                                                        <button
                                                            type="button"
                                                            className="card-info--with-btn"
                                                            onClick={openVacancies}
                                                        >
                                                            <span>Посмотреть</span>
                                                            <ion-icon name="chevron-forward" />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </>
                            )
                        )}

                        {/* VACANCIES LIST */}
                        {(vacanciesIsOpen && company) && (
                            <ul className="card__vacs-list">
                                {company.vacancies.map((vac: VacancyDTO) => (
                                    <li
                                        key={vac.id}
                                        className="card__list-item"
                                        ref={(el) => {
                                            if(el) vacancyRefs.current.set(vac.id, el);
                                            else vacancyRefs.current.delete(vac.id);
                                        }}
                                    >
                                        <button
                                            className={`list-item__button ${(selectedVac?.id === vac.id) ? 'open-vac' : ''}`}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                e.preventDefault();
                                                if(hasVacancyDetails(vac)) {
                                                    toggleVacancy(vac);
                                                }
                                            }}
                                        >
                                            <span className="list-item__title">{vac.name}</span>
                                            {hasVacancyDetails(vac) && (
                                                <ion-icon className={`card__accordion--dropdown__chevron ${(selectedVac?.id === vac.id) ? 'open' : ''}`} name="chevron-forward" />
                                            )}
                                        </button>

                                        <div className={`card__accordion--dropdown__vac ${selectedVac?.id === vac.id ? "" : "hidden"}`}>
                                            <span className="vac-desc">{vac.description || "Описание отсутствует"}</span>

                                            {(vac.employmentType || vac.salary || vac.workSchedule) && (
                                                <div className="vac-dropdown__condition-cards">
                                                    {vac.employmentType && (<span className="condition-card">Ставка: {vac.employmentType}</span>)}
                                                    {vac.workSchedule && (<span className="condition-card">График: {vac.workSchedule}</span>)}
                                                    {vac.salary && (<span className="condition-card">{vac.salary}</span>)}
                                                </div>
                                            )}
                                        </div>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </>
                ) : (
                    <div className="loader-content">
                        <div className="error-icon">!</div>
                        <div className="loader-text">Не удалось загрузить компанию</div>

                        {!isPending && (
                            <button className="error-button" onClick={() => refetch()}>
                                Повторить
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}